import clsx from "clsx";
import { ChevronLeft } from "lucide-react";
import type { ReactNode } from "react";

import { STEPS } from "./onboardingWizardForm";
import useOnboardingTracking from "./useOnboardingTracking";
import { useWizardMode } from "./WizardModeContext";
import { useOnboarding } from "~/hooks";

interface OnboardingWizardStepHeaderProps {
	title?: string;
	subtitle?: ReactNode;
}

const OnboardingWizardStepHeader = ({
	title,
	subtitle,
}: OnboardingWizardStepHeaderProps) => {
	const { step, setStep, learningPath } = useOnboarding();
	const { mode, onExit } = useWizardMode();
	const { track } = useOnboardingTracking();
	const prevStep = STEPS[step].prev(learningPath, mode);

	// On the overlay's first step, "back" leaves the wizard and returns to the
	// launcher (the WelcomeModal options) rather than the wizard's own welcome.
	const isOverlayEntry = mode === "deferred" && prevStep === "welcome";
	const canBack = isOverlayEntry ? !!onExit : !!prevStep;
	const prevTitle = prevStep ? STEPS[prevStep].title : undefined;

	const handleBack = () => {
		if (!prevStep) return;
		track("onboarding:step_back", { from_step: step, to_step: prevStep });
		if (isOverlayEntry) {
			onExit?.();
		} else {
			setStep(prevStep);
		}
	};
	// The deferred (overlay) flow skips the phone step and ends on the auth/signup step.
	// The live flow includes the phone step but ends on the headerless Complete screen.
	// Both flows have 4 total steps with headers.
	const progress = {
		active: STEPS[step].index - 1,
		total: 3,
	};

	return (
		<div className="flex shrink-0 flex-col gap-6">
			{canBack && (
				<button
					type="button"
					onClick={handleBack}
					className="flex items-center gap-1 self-start font-medium text-sm transition hover:underline"
				>
					<ChevronLeft size={16} />
					{prevTitle}
				</button>
			)}

			<div className="flex gap-1.5">
				{Array.from({ length: progress.total }).map((_, i) => (
					<div
						key={i}
						className={clsx(
							"h-1.5 flex-1 rounded-full transition-all duration-300",
							i < progress.active && "bg-sky-300 dark:bg-sky-600",
							i === progress.active &&
								"bg-sky-500 shadow-sky-300/50 shadow-sm dark:bg-sky-500 dark:shadow-sky-600/50",
							i > progress.active && "bg-slate-200 dark:bg-slate-700",
						)}
					/>
				))}
			</div>

			{title && subtitle && (
				<div className="flex flex-col gap-2">
					<h2 className="font-semibold text-black text-xl leading-tight dark:text-white">
						{title}
					</h2>
					<p className="text-slate-500 text-sm dark:text-slate-400">
						{subtitle}
					</p>
				</div>
			)}
		</div>
	);
};

export default OnboardingWizardStepHeader;
